import * as React from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import { ChipButton } from '../../../../components/controls/chipButton';

import './anomalyTypePicker.css';

export type AnomalyType = 'deviceRebooted' | 'deviceLowPower' | 'deviceDropped' | 'totalDataUsage' | 'processCrashed' | 'processHung';

export const anomalyTypesArray: readonly AnomalyType[] = ['deviceRebooted', 'deviceLowPower', 'deviceDropped', 'totalDataUsage', 'processCrashed', 'processHung'];

export const isAnomalyType = (value: string): value is AnomalyType => {
  return (anomalyTypesArray as readonly string[]).includes(value);
};

type Option = { value: AnomalyType | null; label: string };

type AnomalyTypePickerProps = {
  selectedAnomalyType: AnomalyType | null;
  onChange: (type: AnomalyType | null) => void;
  hasWindows: boolean;
  hasAndroid: boolean;
};

export function AnomalyTypePicker({
  selectedAnomalyType,
  onChange,
  hasWindows,
  hasAndroid
}: AnomalyTypePickerProps) {
  const { t } = useTranslation(['translation', 'anomalyDetectionBeta']);

  // Matches useDeviceAnomaliesListColumns.tsx's own column order (totalDataUsage, deviceRebooted,
  // deviceLowPower, deviceDropped, processCrashed, processHung) so the filter chips line up with
  // the columns they filter, left to right.
  const options: Option[] = [
    { value: null, label: t('ALL', { ns: 'translation' }) },
    { value: 'totalDataUsage', label: t('DATA_USAGE', { ns: 'anomalyDetectionBeta' }) },
    { value: 'deviceRebooted', label: t('REBOOTS', { ns: 'anomalyDetectionBeta' }) },
    { value: 'deviceLowPower', label: t('LOW_POWER', { ns: 'anomalyDetectionBeta' }) },
    ...(hasAndroid ? [{ value: 'deviceDropped' as AnomalyType, label: t('DROPS', { ns: 'anomalyDetectionBeta' }) }] : []),
    ...(hasWindows ? [
      { value: 'processCrashed' as AnomalyType, label: t('PROCESS_CRASHES', { ns: 'anomalyDetectionBeta' }) },
      { value: 'processHung' as AnomalyType, label: t('PROCESS_HANGS', { ns: 'anomalyDetectionBeta' }) },
    ] : []),
  ];

  return (
    <div className="anomaly-type-picker">
      {options.map(option => (
        <ChipButton
          key={option.value ?? 'all'}
          text={option.label}
          active={option.value === selectedAnomalyType}
          onClick={() => {
            if (option.value !== selectedAnomalyType) {
              onChange(option.value);
            }
          }}
        />
      ))}
    </div>
  );
}
